import { Link, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Card,
  Button,
  Breadcrumb,
  Col,
  Form
} from "react-bootstrap";
import {
  Page,
  Spinner,
  Divider,
  ErrorMessages,
  IconButton,
  IconButtonType,
  NoData,
  DeleteConfirmation
} from "@whitespace/whitespace-ui";
import { useCallback, useEffect, useState } from "react";
import { ariaInvalidCheck } from "@whitespace/whitespace-utils";
import IEditVehicleRequestModel from "interfaces/Request/IEditVehicleRequestModel";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import vehicleSchema from "modules/schemas/vehicleSchema";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { makeDropDownTreeAACompliant } from "modules/SyncfusionAAFixer";
import { DropDownTreeComponent } from "@syncfusion/ej2-react-dropdowns";
import AmplifyError from "components/AmplifyError/AmplifyError";
import IGetTrackingDevicesResponseModel from "interfaces/Response/IGetTrackingDevicesResponseModel";
import "styles/sf-base.scss";
import "styles/sf-dropdown.scss";
import IRegion from "interfaces/IRegion";
import { useStateMachine } from "little-state-machine";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";

const EditVehicle = () => {
  const { state } = useStateMachine();
  const [vehicle, _] = useState<IEditVehicleRequestModel>({
    ...state.editVehicleRequest!
  });

  const [deleteButtonPressed, setDeleteButtonPressed] = useState<boolean>(false);
  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues,
    watch
  } = useForm<IEditVehicleRequestModel>({
    resolver: yupResolver(vehicleSchema),
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const {
    isSending: getUnallocatedTrackingDevicesIsSending,
    errors: getUnallocatedTrackingDevicesErrors,
    data: getUnallocatedTrackingDevicesData,
    sendRequest: getUnallocatedTrackingDevicesSendRequest
  } = useAmplifyREST();

  const {
    isSending: getRegionsIsSending,
    errors: getRegionsErrors,
    data: getRegionsData,
    sendRequest: getRegionsSendRequest
  } = useAmplifyREST();

  const {
    isSending: editVehicleIsSending,
    errors: editVehicleErrors,
    statusCode: editVehicleStatusCode,
    sendRequest: editVehicleSendRequest
  } = useAmplifyREST();

  const {
    isSending: deleteVehicleIsSending,
    errors: deleteVehicleErrors,
    statusCode: deleteVehicleStatusCode,
    sendRequest: deleteVehicleSendRequest
  } = useAmplifyREST();

  const [aaFixDropDownTreeCompleted, setAAFixDropDownTreeCompleted] = useState<boolean>(false);

  const isSending = getUnallocatedTrackingDevicesIsSending
    || getRegionsIsSending
    || editVehicleIsSending
    || deleteVehicleIsSending
    || !aaFixDropDownTreeCompleted;

  const navigate = useNavigate();

  const [dropDownTreeComponent, setDropDownTreeComponent] = useState<DropDownTreeComponent>();
  const [selectedRegions, setSelectedRegions] = useState<string[] | undefined>(
    vehicle.regions
  );
  const [editButtonDisabled, setEditButtonDisabled] = useState(true);
  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [regions, setRegions] = useState<IRegion[] | undefined>();
  const [apiErrors, setApiErrors] = useState<string[]>([]);

  const regionFields: Object = {
    dataSource: regions,
    value: "id",
    text: "name",
    child: "nodeChild"
  };

  const regionsTreeSettings = { autoCheck: false };

  const canFormBeSubmitted = () => {
    const name = getValues("name");
    const aOmPassword = getValues("aOmPassword");
    const type = getValues("type");
    const active = getValues("active");
    const trackingId = getValues("trackingId");

    const regionsRemoved = vehicle?.regions?.filter((x: any) => !selectedRegions?.includes(x)) ?? [];
    const regionsAdded = selectedRegions?.filter((x: any) => !vehicle?.regions?.includes(x)) ?? [];

    const requiredFieldsEntered = name.length > 0
      && type.length > 0
      && selectedRegions
      && selectedRegions?.length > 0;

    const atLeastOneFieldChangedValue = name !== vehicle.name
      || regionsRemoved?.length > 0
      || regionsAdded?.length > 0
      || (vehicle.name
        ? name !== vehicle.name?.toString()
        : !vehicle.name?.toString() && name.length > 0)
      || (!vehicle.aOmPassword?.toString() && aOmPassword.length > 0)
      || (vehicle.type
        ? type !== vehicle.type?.toString()
        : !vehicle.type?.toString() && type.length > 0)
      || trackingId !== vehicle.trackingId
      || active !== vehicle.active;

    setEditButtonDisabled(
      !(requiredFieldsEntered && atLeastOneFieldChangedValue)
    );
  };

  const watchFields = watch([
    "name",
    "registration",
    "aOmPassword",
    "type",
    "active",
    "trackingId"
  ]);

  useEffect(() => {
    canFormBeSubmitted();
  }, [watchFields]);

  useEffect(() => {
    setSelectedRegions(vehicle?.regions);
  }, [vehicle, setSelectedRegions]);

  useEffect(() => {
    canFormBeSubmitted();
  }, [selectedRegions]);

  const dropDownTreeComponentOnChange = () => {
    setSelectedRegions(dropDownTreeComponent?.value);
  };

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
    setApiErrors([]);
  }, [setShowExceptions, setApiErrors]);

  useEffect(() => {
    getUnallocatedTrackingDevicesSendRequest("tracking/unallocated", "GET");
  }, [getUnallocatedTrackingDevicesSendRequest]);

  useEffect(() => {
    if (getUnallocatedTrackingDevicesErrors) {
      setApiErrors(current => [
        ...current,
        ...getUnallocatedTrackingDevicesErrors
      ]);
      setShowExceptions(true);
    }
  }, [getUnallocatedTrackingDevicesErrors, setApiErrors, setShowExceptions]);

  useEffect(() => {
    getRegionsSendRequest("settings/regions", "GET");
  }, [getRegionsSendRequest]);

  useEffect(() => {
    if (getRegionsData) {
      setRegions(getRegionsData.regions);
    }
  }, [getRegionsData]);

  useEffect(() => {
    if (getRegionsErrors) {
      setApiErrors(current => [...current, ...getRegionsErrors]);
      setShowExceptions(true);
      setAAFixDropDownTreeCompleted(true);
    }
  }, [getRegionsErrors, setApiErrors, setShowExceptions]);

  const handleEditVehicleClick = (request: IEditVehicleRequestModel) => {
    setApiErrors([]);
    request.regions = selectedRegions!;
    editVehicleSendRequest(`settings/vehicles/${vehicle.id}`, "PUT", request);
  };

  useEffect(() => {
    if (editVehicleErrors && editVehicleStatusCode !== 200) {
      setApiErrors(current => [...current, ...editVehicleErrors]);
      setShowExceptions(true);
    }
  }, [
    editVehicleStatusCode,
    editVehicleErrors,
    setApiErrors,
    setShowExceptions
  ]);

  useEffect(() => {
    if (editVehicleStatusCode === 200) {
      navigate(RoutePathsEnum.SettingsCollectors);
    }
  }, [editVehicleStatusCode]);

  useEffect(() => {
    if (dropDownTreeComponent) {
      makeDropDownTreeAACompliant();
      setAAFixDropDownTreeCompleted(true);
    }
  }, [dropDownTreeComponent]);

  const renderTrackingIds = () => {
    const unallocatedOption = (
      <option key="" value="">
        Unallocated
      </option>
    );
    const trackingIdsDropdownList : any[] = [
    ];

    if (!vehicle.trackingId) {
      trackingIdsDropdownList.push(unallocatedOption);
    } else {
      trackingIdsDropdownList.push(
        <option key={vehicle.trackingId} value={vehicle.trackingId}>
          Allocated -
          {" "}
          {vehicle.trackingDeviceName}
        </option>
      );
    }

    if (getUnallocatedTrackingDevicesData) {
      (
        getUnallocatedTrackingDevicesData as IGetTrackingDevicesResponseModel
      ).trackingDevices.forEach(trackingDevice => {
        trackingIdsDropdownList.push(
          <option key={trackingDevice.id} value={trackingDevice.id}>
            {trackingDevice.displayName}
          </option>
        );
      });
    }

    if (vehicle.trackingId) {
      trackingIdsDropdownList.push(unallocatedOption);
    }

    return trackingIdsDropdownList;
  };

  const handleDeleteVehicle = () => {
    deleteVehicleSendRequest(`settings/vehicles/${vehicle.id}`, "DELETE");
  };

  useEffect(() => {
    if (deleteVehicleStatusCode === 200) {
      navigate(RoutePathsEnum.SettingsCollectors);
    }
  }, [deleteVehicleStatusCode]);

  useEffect(() => {
    if (deleteVehicleErrors) {
      setApiErrors(current => [...current, ...deleteVehicleErrors]);
      setShowExceptions(true);
      setDeleteButtonPressed(false);
    }
  }, [
    deleteVehicleErrors,
    setApiErrors,
    setShowExceptions,
    setDeleteButtonPressed
  ]);

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}

      <Divider />
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="edit-vehcile">
              Edit Vehicle
              <Spinner
                animation="grow"
                visible={isSending}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
        </Card.Header>
        <Form
          autoComplete="off"
          onSubmit={handleSubmit(handleEditVehicleClick)}
        >
          <Card.Body className="no-padding">
            <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Breadcrumb className="left-margin-large no-wrap">
                  <Breadcrumb.Item
                    data-testid="breadcrumb-settings"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.Settings }}
                    onClick={() => navigate(RoutePathsEnum.Settings)}
                  >
                    Settings
                  </Breadcrumb.Item>
                  <Breadcrumb.Item
                    data-testid="breadcrumb-collectors"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.SettingsCollectors }}
                    onClick={() => navigate(RoutePathsEnum.SettingsCollectors)}
                  >
                    Collectors
                  </Breadcrumb.Item>
                  <Breadcrumb.Item active data-testid="breadcrumb-edit-vehicle">
                    {`Edit ${vehicle?.name} Vehicle`}
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Col>
              <Col className="d-lg-flex justify-content-end user-filter">
                <IconButton
                  iconType={IconButtonType.Delete}
                  variant="danger"
                  className="right-margin-small"
                  disabled={vehicle.inUse || isSending}
                  aria-disabled={vehicle.inUse || isSending}
                  aria-label="Delete Vehicle"
                  type="button"
                  spin={deleteVehicleIsSending}
                  onClick={() => setDeleteButtonPressed(true)}
                  data-testid="btnDelete"
                >
                  Delete
                </IconButton>
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={editButtonDisabled || editVehicleIsSending}
                  aria-disabled={editButtonDisabled || editVehicleIsSending}
                  aria-label="Edit vehicle"
                  type="submit"
                  spin={isSending}
                  data-testid="edit"
                >
                  Update
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={() => navigate(RoutePathsEnum.SettingsCollectors)}
                  data-testid="cancel"
                  aria-label="Cancel edit and back to collectors"
                >
                  Cancel
                </Button>
              </Col>
            </Row>
            <Divider />
            <section className="pt-2" data-testid="formElements">
              <Container fluid>
                <Row className="d-flex mt-1 ms-1">
                  <Col xs={12}>
                    <div className="control-pane">
                      <div className="control-section" />
                      <Row className="mb-2">
                        <Form.Group as={Col} controlId="name">
                          <Form.Label
                            id="nameLabel"
                            className="horiz-form-label"
                          >
                            Name:
                          </Form.Label>
                          <Col>
                            <Form.Control
                              type="text"
                              role="textbox"
                              defaultValue={vehicle?.name}
                              aria-required="true"
                              aria-invalid={ariaInvalidCheck(
                                "name",
                                errors,
                                null
                              )}
                              aria-labelledby="nameLabel"
                              maxLength={50}
                              {...register("name")}
                            />
                          </Col>
                          <ErrorMessages
                            fieldName="name"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>

                      <Row className="mb-2">
                        <Form.Group as={Col} controlId="registration">
                          <Form.Label
                            id="registrationLabel"
                            className="horiz-form-label"
                          >
                            Registration (Mobile user login):
                          </Form.Label>
                          <Col>
                            <Form.Control
                              type="text"
                              role="textbox"
                              readOnly
                              defaultValue={vehicle?.registration}
                              aria-required="true"
                              aria-invalid={ariaInvalidCheck(
                                "registration",
                                errors,
                                null
                              )}
                              aria-labelledby="registrationLabel"
                              maxLength={50}
                              {...register("registration")}
                            />
                          </Col>
                          <ErrorMessages
                            fieldName="registration"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>

                        <Form.Group as={Col} controlId="password">
                          <Form.Label
                            id="passwordLabel"
                            className="horiz-form-label"
                          >
                            Change Password:
                          </Form.Label>
                          <Col>
                            <Form.Control
                              type="password"
                              data-testid="aomPassword"
                              role="textbox"
                              minLength={6}
                              maxLength={20}
                              aria-invalid={ariaInvalidCheck(
                                "password",
                                errors,
                                null
                              )}
                              aria-required
                              aria-labelledby="passwordLabel"
                              {...register("aOmPassword")}
                            />
                          </Col>
                          <ErrorMessages
                            fieldName="password"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>

                      <Row className="mb-2">
                        <Form.Group as={Col} controlId="type">
                          <Form.Label
                            id="typeLabel"
                            className="horiz-form-label"
                          >
                            Type:
                          </Form.Label>
                          <Col>
                            <Form.Control
                              type="text"
                              role="textbox"
                              defaultValue={vehicle?.type}
                              aria-required="true"
                              aria-invalid={ariaInvalidCheck(
                                "type",
                                errors,
                                null
                              )}
                              aria-labelledby="typeLabel"
                              maxLength={50}
                              {...register("type")}
                            />
                          </Col>
                          <ErrorMessages
                            fieldName="type"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>

                      <Row className="mb-2 mt-3">
                        <Form.Group
                          as={Col}
                          controlId="active"
                          className="d-flex align-items-center"
                        >
                          <Form.Label
                            id="activeLabel"
                            className="d-inline-flex horiz-form-label w-auto mb-0 me-3"
                          >
                            Active:
                          </Form.Label>
                          <Form.Check
                            {...register("active")}
                            type="switch"
                            defaultChecked={vehicle?.active}
                            className="d-inline-flex custom-switch custom-switch-md ms-2 w-auto"
                            aria-required="true"
                            aria-labelledby="activeLabel"
                          />
                          <ErrorMessages
                            fieldName="active"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>

                      <Row className="mb-2">
                        <Form.Group as={Col} controlId="regionsDropDown">
                          <Form.Label
                            id="regionsLabel"
                            className="horiz-form-label"
                          >
                            Region(s):
                          </Form.Label>
                          <Col>
                            {regions && (
                              <DropDownTreeComponent
                                {...register("regions")}
                                ref={(d: DropDownTreeComponent) => {
                                  setDropDownTreeComponent(d);
                                }}
                                value={vehicle?.regions}
                                change={dropDownTreeComponentOnChange}
                                treeSettings={regionsTreeSettings}
                                fields={regionFields}
                                allowMultiSelection
                                showCheckBox
                                changeOnBlur={false}
                                mode="Box"
                                id="regionsDropDown"
                                htmlAttributes={{
                                  "data-testid": "ddlRegions",
                                  "aria-required": "true"
                                }}
                              />
                            )}
                            <NoData
                              visible={
                                !apiErrors && !getRegionsIsSending && !regions
                              }
                              description="No Region Found"
                            />
                          </Col>
                          <ErrorMessages
                            fieldName="regions"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>

                      <Row className="mb-2">
                        <Form.Group as={Col} controlId="trackingId">
                          <Form.Label
                            id="trackingIdLabel"
                            className="horiz-form-label"
                          >
                            Tracking ID:
                          </Form.Label>
                          <Col>
                            <Form.Select
                              {...register("trackingId", {
                                onChange: () => canFormBeSubmitted()
                              })}
                              defaultValue={vehicle.trackingId}
                              data-testid="ddlTrackingId"
                              aria-required={false}
                              aria-invalid={false}
                              aria-labelledby="trackingIdLabel"
                            >
                              {renderTrackingIds()}
                            </Form.Select>
                            <NoData
                              visible={
                                !apiErrors
                                && !getUnallocatedTrackingDevicesIsSending
                                && !getUnallocatedTrackingDevicesData
                              }
                              description="No Unallocated Tracking device Found"
                            />
                          </Col>
                          <ErrorMessages
                            fieldName="trackingId"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Container>
            </section>
          </Card.Body>
        </Form>
      </Card>
      <DeleteConfirmation
        title="Delete Vehicle"
        onDelete={() => {
          handleDeleteVehicle();
        }}
        onHide={() => {
          setDeleteButtonPressed(false);
        }}
        isDeleting={deleteVehicleIsSending}
        show={deleteButtonPressed}
      >
        <p>
          Are you sure you want to delete
          {" "}
          {vehicle.name}
          {" "}
          Vehicle?
        </p>
      </DeleteConfirmation>
    </Page>
  );
};

export default EditVehicle;
